import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { getWeeklyReports, reloadWeeklyReports, getWeeklyReportBreakdown } from './api';
import './PartesSemanales.css';

function PartesSemanales() {
  const [weeklyData, setWeeklyData] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);
  const [weeksToShow, setWeeksToShow] = useState(8); // Configurable por el usuario
  const [expandedRows, setExpandedRows] = useState({}); // Tracks which user-week combinations are expanded
  const [breakdownData, setBreakdownData] = useState({}); // Cache breakdown data by weeklyReportId
  const [loadingBreakdown, setLoadingBreakdown] = useState({}); // Track loading state for breakdowns

  useEffect(() => {
    loadCurrentUser();
    loadWeeklyReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeksToShow]);

  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const user = await response.json();
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadWeeklyReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getWeeklyReports(weeksToShow);
      setWeeklyData(response.data.data || []);
      setLastSync(response.data.lastSync);
    } catch (error) {
      console.error('Error loading weekly reports:', error);
      setError('Error al cargar los partes semanales');
    } finally {
      setLoading(false);
    }
  };

  const handleReload = async () => {
    if (!currentUser || currentUser.role !== 'admin') {
      alert('Solo los administradores pueden recargar los datos');
      return;
    }

    if (!window.confirm(`¿Recargar datos de las últimas ${weeksToShow} semanas desde Redmine? Esto puede tardar unos minutos.`)) {
      return;
    }

    try {
      setReloading(true);
      setError(null);
      setWeeklyData([]); // Limpiar datos antes de recargar
      setBreakdownData({}); // Limpiar cache de breakdown
      setExpandedRows({}); // Colapsar todas las filas

      const response = await reloadWeeklyReports(weeksToShow);
      alert(`Datos recargados exitosamente. ${response.data.recordsProcessed} registros procesados.`);

      // Recargar datos frescos desde la base de datos
      await loadWeeklyReports();
    } catch (error) {
      console.error('Error reloading data:', error);
      setError('Error al recargar datos desde Redmine: ' + (error.response?.data?.details || error.message));
      alert('Error al recargar los datos. Ver consola para más detalles.');
    } finally {
      setReloading(false);
    }
  };

  const loadBreakdown = async (weeklyReportId) => {
    // If already loaded, don't fetch again
    if (breakdownData[weeklyReportId]) {
      return;
    }

    try {
      setLoadingBreakdown(prev => ({ ...prev, [weeklyReportId]: true }));
      const response = await getWeeklyReportBreakdown(weeklyReportId);
      setBreakdownData(prev => ({ ...prev, [weeklyReportId]: response.data }));
    } catch (error) {
      console.error('Error loading breakdown:', error);
      setBreakdownData(prev => ({ ...prev, [weeklyReportId]: [] }));
    } finally {
      setLoadingBreakdown(prev => ({ ...prev, [weeklyReportId]: false }));
    }
  };

  const toggleRow = async (userName, weekStart, weeklyReportId) => {
    const key = `${userName}_${weekStart}`;
    const isCurrentlyExpanded = expandedRows[key];

    if (!isCurrentlyExpanded) {
      // Expanding - load breakdown if not already loaded
      await loadBreakdown(weeklyReportId);
    }

    setExpandedRows(prev => ({
      ...prev,
      [key]: !isCurrentlyExpanded
    }));
  };

  // Organizar datos por semana y usuario
  const organizeDataByWeek = () => {
    if (!weeklyData || !weeklyData.length) {
      return { weeks: [], userNames: [], dataMatrix: {} };
    }

    // Obtener todas las semanas únicas
    const weeksSet = new Set();
    const usersSet = new Set();

    weeklyData.forEach(record => {
      if (record && record.week_start && record.user_name) {
        weeksSet.add(record.week_start);
        usersSet.add(record.user_name);
      }
    });

    const sortedWeeks = Array.from(weeksSet).sort().reverse();
    const sortedUsers = Array.from(usersSet).sort();

    // Crear matriz de datos
    const dataMatrix = {};
    weeklyData.forEach(record => {
      if (record && record.user_name && record.week_start) {
        const key = `${record.user_name}_${record.week_start}`;
        dataMatrix[key] = {
          id: record.id,
          hours: parseFloat(record.hours) || 0,
          entries: parseInt(record.entries_count) || 0
        };
      }
    });

    return {
      weeks: sortedWeeks,
      userNames: sortedUsers,
      dataMatrix
    };
  };

  const { weeks: weeksList = [], userNames = [], dataMatrix = {} } = organizeDataByWeek();

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="partes-semanales-container">
      <div className="partes-header">
        <div>
          <h2>Partes Semanales</h2>
          <p className="partes-subtitle">Datos de imputación de horas desde Redmine (Grupo DevOps)</p>
        </div>
        <div className="partes-controls">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="weeks-selector">
              <label htmlFor="weeks-select">Semanas a mostrar:</label>
              <select
                id="weeks-select"
                value={weeksToShow}
                onChange={(e) => setWeeksToShow(parseInt(e.target.value))}
                disabled={loading || reloading}
              >
                <option value={4}>4 semanas</option>
                <option value={8}>8 semanas</option>
                <option value={12}>12 semanas</option>
                <option value={16}>16 semanas</option>
                <option value={20}>20 semanas</option>
              </select>
            </div>
            <button
              onClick={handleReload}
              className="btn-reload"
              disabled={!isAdmin || reloading}
              title={!isAdmin ? 'Solo administradores pueden recargar datos' : 'Recargar datos desde Redmine'}
            >
              {reloading ? '🔄 Recargando...' : '🔄 Recargar Datos'}
            </button>
            {!isAdmin && <span className="admin-only-badge">Solo Admin</span>}
          </div>
        </div>
      </div>

      {lastSync && (
        <div className="sync-info">
          <span className="sync-label">Última sincronización:</span>
          <span className="sync-date">
            {format(new Date(lastSync.completed_at), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}
          </span>
          <span className="sync-records">({lastSync.records_processed} registros)</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      ) : weeklyData.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No hay datos disponibles</h3>
          <p>No se encontraron partes semanales para el período seleccionado.</p>
          {isAdmin && (
            <button onClick={handleReload} className="btn-reload" disabled={reloading}>
              {reloading ? '🔄 Recargando...' : '🔄 Recargar Datos desde Redmine'}
            </button>
          )}
        </div>
      ) : (
        <div className="partes-table-container">
          <table className="partes-table">
            <thead>
              <tr>
                <th className="user-column">Usuario</th>
                {weeksList.map((weekStart, index) => {
                  const date = parseISO(weekStart);
                  return (
                    <th key={weekStart} className="week-column">
                      <div className="week-header">
                        <div className="week-number">Semana {weeksList.length - index}</div>
                        <div className="week-date">{format(date, 'dd/MM', { locale: es })}</div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {userNames.map(userName => {
                return (
                  <React.Fragment key={userName}>
                    <tr className="user-row">
                      <td className="user-cell">{userName}</td>
                      {weeksList.map(weekStart => {
                        const key = `${userName}_${weekStart}`;
                        const data = dataMatrix[key];
                        const hours = data?.hours || 0;
                        const isExpanded = expandedRows[key];
                        const hasData = hours > 0;

                        return (
                          <td
                            key={weekStart}
                            className={`hours-cell ${hasData ? 'clickable' : ''}`}
                            onClick={() => hasData && toggleRow(userName, weekStart, data.id)}
                            title={hasData ? 'Click para ver desglose de tareas' : ''}
                          >
                            {hours > 0 ? (
                              <div className="hours-display">
                                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                <span className="hours-value">{hours.toFixed(1)}h</span>
                                {data.entries > 0 && (
                                  <span className="entries-count" title={`${data.entries} entradas`}>
                                    ({data.entries})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="hours-empty">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Breakdown rows */}
                    {weeksList.map(weekStart => {
                      const key = `${userName}_${weekStart}`;
                      const data = dataMatrix[key];
                      const isExpanded = expandedRows[key];

                      if (!isExpanded || !data) return null;

                      const breakdown = breakdownData[data.id] || [];
                      const isLoadingBreakdownData = loadingBreakdown[data.id];

                      return (
                        <tr key={`${key}_breakdown`} className="breakdown-row">
                          <td colSpan={weeksList.length + 1}>
                            <div className="breakdown-content">
                              <div className="breakdown-header">
                                <strong>Desglose de {userName} - Semana {format(parseISO(weekStart), 'dd/MM', { locale: es })}</strong>
                              </div>
                              {isLoadingBreakdownData ? (
                                <div className="breakdown-loading">Cargando desglose...</div>
                              ) : breakdown.length === 0 ? (
                                <div className="breakdown-empty">No hay detalles disponibles</div>
                              ) : (
                                <table className="breakdown-table">
                                  <thead>
                                    <tr>
                                      <th>Proyecto</th>
                                      <th>Tarea</th>
                                      <th>Actividad</th>
                                      <th className="breakdown-hours">Horas</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdown.map((task, idx) => (
                                      <tr key={idx}>
                                        <td className="breakdown-project">{task.project_name}</td>
                                        <td className="breakdown-issue">{task.issue_subject}</td>
                                        <td className="breakdown-activity">{task.activity_name}</td>
                                        <td className="breakdown-hours">{parseFloat(task.hours).toFixed(1)}h</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="partes-footer">
        <div className="info-box">
          <h4>ℹ️ Información</h4>
          <ul>
            <li>Los datos se sincronizan desde Redmine bajo demanda (solo admins)</li>
            <li>Los usuarios se obtienen dinámicamente del grupo DevOps en Redmine</li>
            <li>Las horas mostradas corresponden a las entradas de tiempo registradas en Redmine</li>
            <li>Haz click en cualquier celda con horas para ver el desglose de tareas</li>
            <li>El número entre paréntesis indica la cantidad de entradas de tiempo</li>
            <li>Puedes configurar el número de semanas a mostrar (4, 8, 12, 16 o 20)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default PartesSemanales;
